import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { FormControl, TextField } from "@material-ui/core";

import withModal from "../../withModal";
import { hideModal } from "../../../../actions/actionCreators";
import MaterialModal from "../../MaterialModal";

import { fetchT, IRequestResult } from "../../../../helpers/typedFetch";
import h from "../../../../helpers";
import { notifyGreen } from "../../../../actions/notifyActions";

type IFieldApprovalData = {
    FieldApprovalDataId: number;
    CompanyName: string;
    Email: string;
    EmailBody: string;
};

type IGetFieldLabelApprovalData = IRequestResult & {
    fieldApprovalData: IFieldApprovalData;
};

type IModalProps = {
    fieldLabelId: number;
    labelName: string;
    disableEscape: boolean;
};

type IProps = {
    isOpen: boolean;
    modalProps: IModalProps;
};

const FieldLabelApprovalModal: React.FC<IProps> = ({ isOpen, modalProps }: IProps) => {
    const dispatch = useDispatch();

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [companyName, setCompanyName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [isEmailsValid, setIsEmailsValid] = useState(true);
    const [body, setBody] = useState<string>("");

    useEffect(() => {
        if (modalProps.labelName) {
            setCompanyName(modalProps.labelName);
        }
    }, [modalProps.labelName]);

    useEffect(() => {
        if (modalProps.fieldLabelId) {
            getFieldLabelApprovalData(modalProps.fieldLabelId);
        }
    }, [modalProps.fieldLabelId]);

    const handleEmails = (e: any) => {
        validateEmails(e.target.value);
        setEmail(e.target.value);
    };

    function validateEmails(string: any) {
        if (string == undefined || !string.trim()) {
            return setIsEmailsValid(true);
        }

        const regex = /^([\w-\.]+@([\w-]+\.)+[\w-]{2,4})?$/;
        const result = string.replace(/\s/g, "").split(/,|;/);
        for (let i = 0; i < result.length; i++) {
            if (!regex.test(result[i])) {
                return setIsEmailsValid(false);
            }
        }

        return setIsEmailsValid(true);
    }

    const hide = () => {
        cleanup();
        dispatch(hideModal("FIELD_LABEL_APPROVAL"));
    };

    const cleanup = () => {
        setIsSaving(false);
        setIsLoading(false);
        setCompanyName("");
        setEmail("");
        setBody("");
    };

    const getFieldLabelApprovalData = fieldLabelId => {
        const url = `/Field/GetFieldLabelApprovalData/${fieldLabelId}`;

        setIsLoading(true);
        fetchT<IGetFieldLabelApprovalData>(url, "POST")
            .then(data => {
                if (data.success) {
                    if (data.fieldApprovalData) {
                        setCompanyName(data.fieldApprovalData.CompanyName);
                        setEmail(data.fieldApprovalData.Email);
                        setBody(data.fieldApprovalData.EmailBody);
                    } else {
                        setEmail("");
                        setBody("");
                    }
                } else {
                    h.error("There was an error loading the label approval data.");
                }

                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                h.error("Error getting label approval data.");
            });
    };

    const save = () => {
        const url = `/Field/UpdateFieldLabelApprovalData`;

        const formData = new FormData();
        formData.append("fieldLabelId", modalProps.fieldLabelId.toString());
        formData.append("companyName", companyName);
        formData.append("email", email);
        formData.append("body", h.htmlEncode(body));

        setIsSaving(true);
        fetchT<IRequestResult>(url, "POST", formData)
            .then(data => {
                if (data.success) {
                    hide();
                    dispatch(notifyGreen("Label approval settings have been saved successfully."));
                } else {
                    h.error("There was an error saving the label approval data.");
                }
                setIsSaving(false);
            })
            .catch(() => {
                h.error("Error saving label approval data.");
                setIsSaving(false);
            });
    };

    return (
        <MaterialModal
            id="variable-admin-modal"
            isOpen={isOpen}
            onClose={hide}
            title="Field Approval Settings"
            onPrimaryButtonRequest={save}
            showPrimaryButton={true}
            cancelButtonText="Close"
            isPrimaryButtonDisabled={isLoading || isSaving || !companyName || !isEmailsValid || !email || !body}
            disableEscape={modalProps.disableEscape}
        >
            <div>
                <FormControl style={{ minWidth: "15vw", paddingRight: "10px" }}>
                    <TextField
                        inputProps={{ style: { fontSize: "15px" } }}
                        label={"Company Name *"}
                        type="text"
                        value={companyName}
                        onChange={e => setCompanyName(e.target.value)}
                        disabled={isLoading || isSaving}
                    />
                </FormControl>
                <FormControl style={{ minWidth: "15vw", paddingRight: "10px", marginTop: "10px" }}>
                    <TextField
                        error={!isEmailsValid}
                        inputProps={{ style: { fontSize: "15px" } }}
                        label={"Email To *"}
                        type="text"
                        value={email}
                        onChange={handleEmails}
                        disabled={isLoading || isSaving}
                        helperText={
                            isEmailsValid
                                ? "You can add multiple emails seperated by a semicolon."
                                : "You can add multiple emails seperated by a semicolon. Emails are invalid"
                        }
                    />
                </FormControl>
                <div style={{ marginTop: "20px" }}>
                    <TextField
                        label="Email Body *"
                        multiline
                        rows={25}
                        value={body}
                        className="wiki-editor"
                        onChange={e => setBody(e.target.value)}
                        type="text"
                        disabled={isLoading || isSaving}
                        variant="outlined"
                    />
                </div>
            </div>
        </MaterialModal>
    );
};
export default withModal(FieldLabelApprovalModal, "FIELD_LABEL_APPROVAL");
